/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import styled from "styled-components";
import { Box, SVGIcon, Text, theme } from "@nordcloud/gnui";
import FlexContainer from "~/components/Styled/FlexContainer";

export const AdditionalInfo = styled.div`
  width: 13rem;
  padding-left: 1rem;
  margin-left: auto;
  padding-right: 0.75rem;
  text-align: right;
  border-left: 1px solid ${theme.color.border.border01};
`;

type CollapseItemProps = {
  name: React.ReactNode;
  isRemoved?: boolean;
  isNarrow?: boolean;
  buttons?: React.ReactNode;
  children: React.ReactNode;
};
type ButtonChevronProps = {
  isChildren: boolean;
  isExpanded: boolean;
};

export const ButtonChevron = styled(SVGIcon)<ButtonChevronProps>`
  cursor: pointer;
  margin-right: ${theme.spacing.spacing04};
  max-width: 1.5rem;
  width: 1.5rem;
  max-height: 1.5rem;
  height: 1.5rem;
  flex: 1 0 1.5rem;
  transition: transform 0.1s linear;
  opacity: ${({ isChildren }) => (isChildren ? 1 : 0.2)};
  pointer-events: ${({ isChildren }) => (isChildren ? "auto" : "none")};
  transform: ${({ isExpanded }) =>
    isExpanded ? "rotate(0deg)" : "rotate(-90deg)"};
`;

function CollapseItem({
  children,
  name,
  buttons,
  isRemoved,
  isNarrow,
}: CollapseItemProps) {
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

  React.useEffect(() => {
    !children && setIsExpanded(false);
  }, [children]);

  return (
    <Box
      boxStyle="lightGrey"
      backgroundColor={
        isRemoved ? theme.color.background.ui01 : theme.color.background.ui03
      }
      spacing="spacing02"
    >
      <Row css={{ opacity: isRemoved ? 0.6 : 1 }}>
        {isRemoved ? (
          <Col sm={8} md={8} lg={12}>
            <FlexContainer grow>
              <ButtonChevron
                name="chevronDown"
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
                isChildren={!!children}
                isExpanded={isExpanded}
              />
              {name}
            </FlexContainer>
          </Col>
        ) : (
          <Col
            sm={isNarrow ? 8 : 3}
            md={isNarrow ? 8 : 4}
            lg={isNarrow ? 12 : 6}
          >
            <FlexContainer>
              <ButtonChevron
                name="chevronDown"
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
                isChildren={!!children}
                isExpanded={isExpanded}
                aria-label="toggle"
              />
              <Text size="lg" weight="medium" tag="div">
                {name}
              </Text>
            </FlexContainer>
          </Col>
        )}
        {!isRemoved && (
          <Col sm={5} md={4} lg={6}>
            <FlexContainer grow justify="flex-end">
              {buttons}
            </FlexContainer>
          </Col>
        )}
      </Row>

      {isExpanded && <div>{children}</div>}
    </Box>
  );
}

export { CollapseItem };
